import styled, {css} from 'styled-components/native';
import React from 'react';
import {tr} from '../../translations';
import TextGeneric from '../textGeneric';
import ProgressiveImage from '../progressiveImage';
import {LinearGradient} from 'react-native-linear-gradient';
import {useTheme} from 'styled-components';
import {Theme} from '../../theme/lightTheme';
import TextInput from '../textInput';
import Icon from '../icon';
import {TouchableOpacity} from 'react-native';
import GenericButton from '../button';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';
import {TabNewsSearchProps} from '../../interfaces/components/tabNewsSearch';
import {useCustomHeaderHeight} from '../../utils/hooks/useHeaderHeight';
import Animated, {FadeInLeft} from 'react-native-reanimated';
import {isWeb} from '../../utils/responsive';

const Container = styled.View`
  width: 100%;
  border-bottom-right-radius: 33px;
  border-bottom-left-radius: 33px;
  overflow: hidden;
`;
const CurveContainer = styled(Animated.View)<{
  topInsets: number;
  responsiveWidth: string;
}>`
  width: ${({responsiveWidth}) => (responsiveWidth ? responsiveWidth : '100%')};
  height: 100%;
  position: absolute;
  top: ${({topInsets}) => topInsets}px;
  left: 0;
  z-index: 0;
  align-self: center;
`;
const Image = styled(ProgressiveImage)`
  width: 100%;
  height: 100%;
`;
const ContentContainer = styled.View<{responsiveWidth: string}>`
  width: ${({responsiveWidth}) => (responsiveWidth ? responsiveWidth : '100%')};
  align-self: center;
`;
const TitleContainer = styled.View`
  width: 90%;
  align-self: center;
  padding-top: ${({theme}) => theme.text.s55}px;
  z-index: 1;
`;
const Title = styled(TextGeneric)`
  color: ${({theme}) => theme.text.light};
  font-size: ${({theme}) => theme.text.s2}px;
  line-height: ${({theme}) => theme.text.s2 * 1.1}px;
`;
const SearchInput = styled(TextInput)`
  color: ${({theme}) => theme.text.light};
`;
const Row = styled.View`
  flex-direction: row;
  width: 85%;
  align-self: center;
  align-items: center;
  justify-content: space-between;
`;
const IconContainer = styled.TouchableOpacity`
  background-color: ${({theme}) => theme.text.light};
  border-radius: 100px;
  align-items: center;
  justify-content: center;
  width: ${({theme}) => theme.text.s1 * 1.5}px;
  height: ${({theme}) => theme.text.s1 * 1.5}px;
  shadow-color: ${({theme}) => theme.text.dark};
  shadow-offset: 2px 4px;
  shadow-opacity: 0.1;
  shadow-radius: 4px;
  elevation: 1;
`;
const ConfirmSelection = styled(GenericButton)<{buttonWidth: string}>`
  width: ${({buttonWidth}) => buttonWidth};
  align-self: center;
  background-color: ${({theme}) => theme.tabNews.button};
  margin-top: ${({theme}) => theme.text.s55}px;
  margin-bottom: ${({theme}) => theme.text.s55}px;
  ${isWeb &&
  css`
    box-shadow: 2px 4px 12px rgba(0, 0, 0, 0.3);
  `}
`;
/**
 * `TabNewsSearch` is a React component.
 *
 * @component
 * @returns {React.ReactElement} A React functional component that renders the tab item with its label and stylized appearance.
 */
const TabNewsSearch: React.FC<TabNewsSearchProps> = ({
  style,
  title,
  onFilterPress,
  search,
  setSearch,
  onSpeakPress,
  onSubmitSearch,
  onResetPress,
}: TabNewsSearchProps): React.ReactElement => {
  const theme = useTheme() as Theme;
  const {hdp, getByScreenSize} = useResponsiveDimensions();
  const headerHeight = useCustomHeaderHeight();

  const icons = [
    {label: 'advanceSearch', onPress: onFilterPress},
    {label: 'speak', onPress: onSpeakPress},
    {label: 'resetSearch', onPress: onResetPress},
  ];

  const _renderButton = (item: any) => {
    if (item.label === 'speak') {
      return (
        <TouchableOpacity
          key={item.label}
          onPress={item?.onPress}
          activeOpacity={0.7}>
          <Icon name={item.label} size={theme.text.s1 * 1.6} />
        </TouchableOpacity>
      );
    }
    return (
      <IconContainer
        key={item.label}
        onPress={item?.onPress}
        activeOpacity={0.7}>
        <Icon name={item.label} size={theme.text.s3} color={theme.primary} />
      </IconContainer>
    );
  };
  return (
    <Container style={style}>
      <LinearGradient
        colors={[
          theme.tabNews.startLinearGradient,
          theme.tabNews.secondLinearGradient,
          theme.tabNews.thirdLinearGradient,
        ]}
        start={{x: 1, y: 0}}
        end={{x: 1, y: 1}}
        style={{
          flex: 1,
          paddingTop: Math.max(headerHeight, hdp(1)),
        }}>
        <CurveContainer
          responsiveWidth={getByScreenSize('100%', '50%')}
          entering={FadeInLeft}
          topInsets={(headerHeight || 0) - theme.text.s7}>
          <Image
            hideLoader
            resizeMode={getByScreenSize('stretch', 'cover')}
            defaultImage={require('../../assets/images/searchCurve.png')}
          />
        </CurveContainer>
        <ContentContainer responsiveWidth={getByScreenSize('100%', '40%')}>
          <TitleContainer>
            <Title bold>{tr('app.appName')}</Title>
            <Title bold>{title || ''}</Title>
          </TitleContainer>
          <SearchInput
            placeholder={tr('tabNews.searchInputPlaceholder')}
            placeholderTextColor={theme.text.light}
            value={search || ''}
            onChangeText={setSearch}
            style={{
              color: theme.text.light,
            }}
            containerStyle={{
              marginVertical: theme.text.s55,
              width: '90%',
              alignSelf: 'center',
            }}
            inputContainerStyle={{
              borderColor: theme.text.light,
              backgroundColor: 'transparent',
              paddingVertical: 2,
              borderRadius: 100,
            }}
            showLeftIcon
            leftIconName={'search'}
            leftIconColor={theme.text.light}
            leftIconSize={theme.text.s5}
          />
          <Row>{icons.map(_renderButton)}</Row>
          <ConfirmSelection
            buttonWidth={getByScreenSize('90%', '50%')}
            isSmall={getByScreenSize(false, true)}
            label={tr('tabNews.submitSearch')}
            disabled={search.length === 0}
            onPress={onSubmitSearch}
            radius={100}
          />
        </ContentContainer>
      </LinearGradient>
    </Container>
  );
};

export default TabNewsSearch;
